import {Injectable} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {select, Store} from '@ngrx/store';
import {CommonConfigurator, CommonConfiguratorUtilsService} from '@spartacus/product-configurator/common';
import {
  Configurator,
  ConfiguratorActions,
  ConfiguratorCartService,
  ConfiguratorCommonsService,
  ConfiguratorSelectors,
  StateWithConfigurator,
} from '@spartacus/product-configurator/rulebased';
import {BehaviorSubject, Observable, Subscription, of} from 'rxjs';
import {debounceTime, filter, map, switchMap, take, tap} from 'rxjs/operators';
import {DcpConfiguratorUtilsService} from './dcp-configurator-utils.service';
import {ActiveCartFacade} from '@spartacus/cart/base/root';
import {PendingConfiguratorRequestService} from './pending-configurator-request.service';
import {DcpApiService} from './dcp-api.service';
import {ConfigLoadingState} from '../models';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpConfiguratorCommonsService extends ConfiguratorCommonsService {
  constructor(
    protected override store: Store<StateWithConfigurator>,
    protected override commonConfigUtilsService: CommonConfiguratorUtilsService,
    protected override configuratorCartService: ConfiguratorCartService,
    protected activeCartFacade: ActiveCartFacade,
    protected override configuratorUtils: DcpConfiguratorUtilsService,
    protected dcpApiService: DcpApiService,
    protected pendingConfiguratorRequestService: PendingConfiguratorRequestService,
  ) {
    super(store, commonConfigUtilsService, configuratorCartService, activeCartFacade, configuratorUtils);
  }

  private initialLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private configReseted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private updateFinished: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public skipConfigurator: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private initialLoadingSubscription: Subscription = Subscription.EMPTY;
  private wasForceReseted = false;
  private configurationStateSelector = (ownerKey: string) =>
    ConfiguratorSelectors.getConfigurationProcessLoaderStateFactory(ownerKey);

  updateMultipleAttributesOfConfiguration(
    ownerKey: string,
    changedAttributes: Configurator.Attribute[],
    updateType?: Configurator.UpdateType,
  ): void {
    if (!updateType) {
      updateType = Configurator.UpdateType.ATTRIBUTE;
    }
    this.setInitialLoadingState(true);
    // in case cart updates pending: Do nothing, because an addToCart might
    // be in progress. Can happen if on slow networks addToCart was hit and
    // afterwards an attribute was changed before the OV navigation has
    // taken place
    this.activeCartFacade
      .getActive()
      .pipe(
        take(1),
        switchMap((cart) =>
          this.activeCartService.isStable().pipe(
            take(1),
            filter((stable) => !cart.code || stable),
            switchMap(() => this.store.pipe(select(ConfiguratorSelectors.getConfigurationFactory(ownerKey)), take(1))),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe((configuration) => {
        this.store.dispatch(
          new ConfiguratorActions.UpdateConfiguration(
            this.configuratorUtils.createConfigurationExtractFromMultipleAttributes(
              changedAttributes,
              configuration,
              updateType,
            ),
          ),
        );
      });
  }

  removeBackendConfiguration(owner: CommonConfigurator.Owner): void {
    this.wasForceReseted = true;
    this.forceNewConfiguration(owner);
    this.setConfigResetedState(true);
    this.wasForceReseted = false;
  }

  getState(): ConfigLoadingState {
    return {
      initialLoading: this.initialLoading,
      configReseted: this.configReseted,
      updateFinished: this.updateFinished,
    };
  }

  setInitialLoadingState(newState: boolean) {
    this.initialLoading.next(newState);
    if (newState && this.initialLoadingSubscription.closed) {
      this.initialLoadingSubscription = this.pendingConfiguratorRequestService
        .hasPendingRequests()
        .pipe(
          untilDestroyed(this),
          debounceTime(400),
          filter((hasPendingRequests) => !hasPendingRequests),
          take(1),
        )
        .subscribe(() => {
          this.setInitialLoadingState(false);
        });
    }
  }

  setConfigResetedState(newState: boolean) {
    this.configReseted.next(newState);
  }

  setUpdateFinishedState(newState: boolean) {
    this.updateFinished.next(newState);
  }

  /* called each time productConfigurator state changes */
  override getOrCreateConfigurationForProduct(
    owner: CommonConfigurator.Owner,
    configIdTemplate?: string,
  ): Observable<Configurator.Configuration> {
    return this.store.pipe(
      select(this.configurationStateSelector(owner.key)),
      tap((configurationState) => {
        if (
          (configurationState.value === undefined ||
            !this.configuratorUtils.isConfigurationCreated(configurationState.value)) &&
          configurationState.loading !== true &&
          configurationState.error !== true &&
          !this.wasForceReseted
        ) {
          this.store.dispatch(
            new ConfiguratorActions.CreateConfiguration({
              owner,
              configIdTemplate,
            }),
          );
        }
      }),
      filter(
        (configurationState) =>
          configurationState.value !== undefined &&
          this.configuratorUtils.isConfigurationCreated(configurationState.value),
      ),
      map((configurationState) => this.configuratorUtils.getConfigurationFromState(configurationState)),
    );
  }
}
